import { useMemo } from 'react';
import { XmlStructureOrderInfoData } from '../../../xml/model/xml-structure-model';
import { convertResponseToOrderData } from '../../pages/order-preparation/convert-response-to-order-data';
import { Row, RowMulti, Section } from './order-detail-confirm-row';
import { orderMethodNameById, getDescriptionInfoTypes, getAdditionalInfoLabel } from './order-detail-confirm-helpers';
import { ConfirmNameResolvers } from './use-confirm-name-resolver';
import { useAppSelector } from '../../../app/hooks';
import { OptionOrderDisplay } from '../../../manager/visible-condition-value-getter';

type Props = {
  order: XmlStructureOrderInfoData,
  // 同 shop_order 配下の全 order-info-data（オプション抽出用）
  allInfoData: XmlStructureOrderInfoData[],
  // 親(OrderDetailConfirmDialog)で先読みした名前解決関数群
  resolvers: ConfirmNameResolvers,
};

const renderOption = (
  opt: XmlStructureOrderInfoData,
  idx: number,
  resolvePageType: (productNameId?: string, parentFlg?: '1' | '2', pageTypeId?: string) => string | undefined,
  optionDisplay?: OptionOrderDisplay,
) => {
  const v = opt.xml.viewModel;
  // オプションのパーツは本身でも type='main' を持たない(type は親注文の本身のみ)。
  // 従来の find(!type) は partsData[0] を一律「表紙」として拾ってしまい、
  // 表紙が無く本身のみのオプションでは本身の値が表紙欄に表示されていた。
  // contentGroupID (op01o_parts1=表紙 / op01o_parts2=本身) で正しく判定する。
  const coverPart = opt.parts?.partsData
    ?.find((p) => (p.xml.rootTagModel?.contentGroupID || '').startsWith('op01o_parts1'))?.xml.viewModel;

  // 記載箇所(pageType)は XML に ID しか無いため、オプション商品単位(parentFlg='2')で API 解決した結果を引く。
  const descriptionLocations = v.descriptionInfo?.pageType
    ? Object.keys(v.descriptionInfo.pageType).map((id) => resolvePageType(v.item?.id, '2', id))
    : [];

  // ラミ選択 / 表紙加工 / 箔押し文字 は注文作成画面では visibleCondition.optionOrderDisplay で表示制御している。
  // XML には表示条件に関わらず値が保存される(オプションのラミは createOrderPartsDataViewModel で
  // 表示条件を見ずに書き込まれる)ため、データ有無だけで描画すると作成画面で出ていない項目が
  // 確認ダイアログに出てしまう。作成画面と同じ表示条件でゲートする。
  // optionDisplay が無い(visibleCondition 未取得など)場合は従来通りデータ有無で表示する。
  const showLaminate = optionDisplay ? Boolean(optionDisplay.laminate) : true;
  const showLeafColor = optionDisplay ? Boolean(optionDisplay.leafColor) : true;
  const showLeafText1 = optionDisplay ? Boolean(optionDisplay.leafText1) : true;
  const showLeafText2 = optionDisplay ? Boolean(optionDisplay.leafText2) : true;
  // 記載箇所も同様。オプションの descriptionInfo.pageType は親注文の記載箇所を継承して
  // XML に書き込まれる(xml-order-info-data-model の descriptionInfoPageTypeInherit)ため、
  // オプション自身で入力していなくても値が入る。
  // さらに注文作成画面では、オプション商品に「親を継承しないパーツ」(inheritParent が
  // 'true' 以外)が 1 つ以上ある場合のみ記載箇所 UI を表示する(option-order.tsx)。
  // 全パーツが親継承(inheritParent='true')のオプションは、XML 上は親の記載箇所を継承して
  // いても作成画面には出ないため、確認ダイアログでも出さない。
  const hasNonInheritingPart = Boolean(opt.parts?.partsData?.some((p) => p.xml.viewModel.inheritParent !== 'true'));
  const showDescriptionLocations = (optionDisplay ? Boolean(optionDisplay.descriptionInfo) : true) && hasNonInheritingPart;

  return (
    <Section key={`option-${idx}`} title={`オプション${idx + 1}`}>
      {Row({ label: '絞り込み条件', value: v.productTagId })}
      {/* オプション商品名は XML 保存値(item.name)を使う。 */}
      {Row({ label: '商品選択', value: v.item?.name })}
      {Row({ label: '数量', value: v.infoOutputCount })}

      {showLaminate && coverPart?.infoSurface?.name
        ? Row({ label: 'ラミ選択', value: coverPart.infoSurface.name })
        : null}
      {showLeafColor && coverPart?.leaf?.colorName
        ? Row({ label: '表紙加工', value: coverPart.leaf.colorName })
        : null}
      {showLeafText1 ? Row({ label: '箔押し文字1', value: coverPart?.leaf?.text1 }) : null}
      {showLeafText2 ? Row({ label: '箔押し文字2', value: coverPart?.leaf?.text2 }) : null}

      {showDescriptionLocations ? RowMulti({ label: '記載箇所', values: descriptionLocations }) : null}
    </Section>
  );
};

export const OrderDetailConfirmView = (props: Props) => {
  const { order, allInfoData, resolvers } = props;
  const masterProductItemInfo = useAppSelector((state) => state.common.data.masterProductItemInfo);
  const visibleCondition = useAppSelector((state) => state.createOrder.visibleCondition);

  const v = order.xml.viewModel;
  const meta = order.xml.metaModel;
  // 本身パーツ(生 XML)。付加情報は複数値を取り得るが convertResponseToOrderData が
  // 1 件に丸めてしまうため、付加情報・ページ数だけはここから直接参照する。
  const genuine = order.parts?.partsData?.find((p) => p.xml.viewModel && p.xml.viewModel.type === 'main')?.xml.viewModel;
  const options = allInfoData.filter((d) => d.xml.metaModel.parentId === meta.id);
  const productNameId = v.item?.id;

  // 表示値は「注文編集」ボタンと同じ復元ロジック(convertResponseToOrderData)で算出する。
  // これにより確認ダイアログと注文編集画面の表示が必ず一致する。
  // convertResponseToOrderData は純粋関数で API は呼ばない。
  // ※レンダー中に呼ぶため、パース不整合な注文で例外が出てもダイアログ全体を
  //   巻き込まないよう try/catch でガードする(その場合は生 XML 由来の項目のみ表示)。
  const orderData = useMemo(() => {
    try {
      return convertResponseToOrderData(order, allInfoData, visibleCondition);
    } catch (e) {
      console.error('[OrderDetailConfirmView] convertResponseToOrderData failed', e);
      return {} as ReturnType<typeof convertResponseToOrderData>;
    }
  }, [order, allInfoData, visibleCondition]);
  const od = orderData.itemDetail;
  const odCover = orderData.cover;
  const odGenuine = orderData.genuine;
  const odDescription = orderData.descriptionInfo;
  // SAP カテゴリか。表紙色は SAP=「商品詳細の表紙色選択」/ 非SAP=「表紙セクションの色」で出し分ける
  // (作成画面の visibleCondition: 商品詳細=op01_coverColor_pre_select は o_isCategorySap 条件、
  //  表紙の色=op01c_coverColor は非SAP)。両者は同じ cover.colorName を読むため、これで重複表示を防ぐ。
  const proFlg = Boolean(orderData.orderInfo?.proFlg);

  // ID -> 名前 解決 (デザインタイプ1 / 付加情報 / 商品名 / 表紙 / 部材 / 記載箇所)。
  // 親(OrderDetailConfirmDialog)で先読み済みの resolvers を、当該注文のコンテキストで引く。
  // XML に名前がある項目は XML 優先、ID しか無い項目のみ個別 API。
  const designType1Name = resolvers.designType1Name(productNameId, v.designType?.templateGroupId);
  const resolvedItemName = resolvers.resolveItemName(v.category?.id, v.productTagId || undefined, od?.coverSelect?.value || undefined, v.coverColorId || undefined, productNameId, od?.itemSelect?.label);
  const hasResolvedAdditionalInfo = resolvers.hasResolvedAdditionalInfo(productNameId);
  const resolveAdditionalInfo = (id: string) => resolvers.resolveAdditionalInfo(productNameId, id);
  const coverName = (coverId?: string) => resolvers.coverName(v.category?.id, v.productTagId || undefined, coverId);
  const boardName = (boardId?: string) => resolvers.boardName(productNameId, boardId);
  const resolvePageType = resolvers.resolvePageType;

  // 商品記載情報も convertResponseToOrderData の結果(親/オプションの選択込み)をそのまま使う。
  // 記載箇所は XML に ID しか無いため、親商品単位(parentFlg='1')で API 解決した結果を引く。
  const descriptionLocations = (odDescription?.descriptionLocations || [])
    .map((id) => resolvePageType(productNameId, '1', id));

  // 商品記載情報の type/option (masterProductItemInfo + productNameId + categoryId から導出)。
  // これに基づいて 名前 / 名前(半角英数) / ふりがな / 日付 / 撮影場所 / コメント / 住所 のどれが表示されるかを決める。
  const descriptionTypes = getDescriptionInfoTypes(masterProductItemInfo, productNameId, v.category?.id);
  const nameOption = descriptionTypes.find((d) => d.type === 'name')?.option;
  const dateOption = descriptionTypes.find((d) => d.type === 'date')?.option;
  const locationOption = descriptionTypes.find((d) => d.type === 'location')?.option;
  const nameLabel = nameOption === 'restrict' ? '名前(半角英数)' : nameOption === 'nenga' ? '名前' : nameOption === '' ? '名前' : null;
  const dateLabel = dateOption === 'kana' ? 'ふりがな' : dateOption === '' ? '日付' : null;
  const locationLabel = locationOption === 'comment' ? 'コメント' : locationOption === 'address' ? '住所' : (locationOption === '' || locationOption === 'height') ? '撮影場所' : null;

  // 本身 - 付加情報系のラベル判定
  const additionalLabel = getAdditionalInfoLabel(
    hasResolvedAdditionalInfo || Boolean((genuine?.additionalInfo || []).length),
    v.category?.id,
    v.item?.id,
    v.goods?.productType,
  );

  return (
    <div className="create_order order_detail_confirm_view">
      <div className="create_order__category">
        <Section title="商品情報">
          {Row({ label: 'カテゴリ', value: v.category?.name || v.category?.id })}
          {Row({ label: '絞り込み条件', value: v.productTagId })}
        </Section>

        <Section title="商品詳細">
          {Row({ label: '表紙選択', value: coverName(od?.coverSelect?.value) })}
          {/* 表紙色選択(商品詳細)は SAP カテゴリのみ。非SAPは表紙セクションの「色」で表示する */}
          {proFlg ? Row({ label: '表紙色選択', value: od?.coverColorSelect?.label }) : null}
          {Row({ label: '商品選択', value: resolvedItemName })}
          {Row({ label: '部材選択', value: boardName(od?.boardSelect?.value) })}
          {Row({ label: '注文方法', value: od?.orderMethod?.label || orderMethodNameById(od?.orderMethod?.value) })}
          {Row({ label: 'デザインタイプ1', value: designType1Name })}
          {/* designType2.value は templateId(ID)。名前(label)が無ければ何も出さない。 */}
          {Row({ label: 'デザインタイプ2', value: od?.designType2?.label })}
          {Row({ label: '数量', value: od?.quantity || undefined })}
          {Row({ label: 'オーダースタイル', value: v.purpose?.name })}
        </Section>

        {odDescription ? (
          <Section title="商品記載情報">
            {nameLabel && odDescription.name ? Row({ label: nameLabel, value: odDescription.name }) : null}
            {dateLabel && odDescription.date?.value ? Row({ label: dateLabel, value: odDescription.date.value }) : null}
            {locationLabel && odDescription.photographLocation ? Row({ label: locationLabel, value: odDescription.photographLocation }) : null}
            {descriptionLocations.length ? RowMulti({ label: '記載箇所', values: descriptionLocations }) : null}
          </Section>
        ) : null}

        {odCover ? (
          <Section title="表紙">
            {odCover.laminate?.label
              ? Row({ label: 'ラミ選択', value: odCover.laminate.label })
              : null}
            {/* 表紙セクションの色は非SAPのみ。SAPは商品詳細の「表紙色選択」で表示済み(重複防止) */}
            {!proFlg && odCover.color?.label
              ? Row({ label: '色', value: odCover.color.label })
              : null}
            {odCover.coverProcessing?.label
              ? Row({ label: '表紙加工', value: odCover.coverProcessing.label })
              : null}
            {Row({ label: '箔押し文字1', value: odCover.leafText1 })}
            {Row({ label: '箔押し文字2', value: odCover.leafText2 })}
          </Section>
        ) : null}

        {odGenuine ? (
          <Section title="本身">
            {odGenuine.laminate?.label
              ? Row({ label: 'ラミ選択', value: odGenuine.laminate.label })
              : null}
            {odGenuine.genuineProcessing?.label
              ? Row({ label: '加工情報', value: odGenuine.genuineProcessing.label })
              : null}
            {Row({ label: '箔押し文字1', value: odGenuine.leafText1 })}
            {Row({ label: '箔押し文字2', value: odGenuine.leafText2 })}
            {/*
              注文作成画面では visibleCondition により 見返し選択 / 台紙貼り / テープ選択 / 付加情報 の 4 ラベルのいずれかが表示される。
              category / goods_id / productType から判定 (getAdditionalInfoLabel)。
              名前解決は ApiEndpaper(productNameId, parentFlg='1') の結果から引く。
              付加情報は複数値を取り得るため convertResponseToOrderData ではなく生 XML(genuine)から参照する。
            */}
            {additionalLabel
              ? RowMulti({ label: additionalLabel, values: (genuine?.additionalInfo || []).map((id) => resolveAdditionalInfo(id)) })
              : null}
            {/* chronologies.value は chronologyID(ID)。名前(label)が無ければ何も出さない。 */}
            {odGenuine.chronologies?.label
              ? Row({ label: '年表選択', value: odGenuine.chronologies.label })
              : null}
            {Row({ label: 'ページ数', value: genuine?.pageCount })}
          </Section>
        ) : null}

        {options.map((opt, i) => renderOption(opt, i, resolvePageType, visibleCondition?.optionOrderDisplay?.[i]))}

        {v.remarks ? (
          <Section title="備考">
            <div className="top_label_ui order_category__item order_detail_confirm_view__row">
              <div className="order_detail_confirm_view__value order_detail_confirm_view__value--multiline">{v.remarks}</div>
            </div>
          </Section>
        ) : null}

        {v.mailRemarks ? (
          <Section title="メール記載情報">
            <div className="top_label_ui order_category__item order_detail_confirm_view__row">
              {/*
                mailRemarks は XML 上 \r\n がエスケープ(\\r\\n)されたまま保存されており、
                order-info-data-xml の split() でも(remarks と違い)アンエスケープされない。
                ここで実改行に戻して表示する。
              */}
              <div className="order_detail_confirm_view__value order_detail_confirm_view__value--multiline">{v.mailRemarks.replace(/\\r\\n/g, '\n')}</div>
            </div>
          </Section>
        ) : null}
      </div>
    </div>
  );
};
